/**
 * Auth API — wraps the first-party LOS `/auth/*` endpoints (login, me, logout).
 * Module-level (usable outside React) via the shared dsHttp client, which
 * injects the bearer token + X-Role automatically.
 *
 * Paths follow loan_and_auth_endpoints.md §2:
 *   - password login  → POST /auth/login/password  { email, password }
 *   - email OTP        → POST /auth/otp/request     { email }            (always 202)
 *                        POST /auth/otp/verify      { email, code }
 * (These replaced the older digital-sizer aliases `/auth/password-login`,
 * `/auth/send-code`, `/auth/verify-code`.)
 */
import { dsHttp } from "../lib/digitalSizerHttp";

export const authApi = {
  // POST /auth/otp/request { email } -> 202 (always; never reveals existence)
  sendCode: (email) =>
    dsHttp.post("/auth/otp/request", { email }).then((r) => r.data),
  // POST /auth/otp/verify { email, code } -> { access_token, ... } (+cookie)
  verifyCode: (email, code) =>
    dsHttp.post("/auth/otp/verify", { email, code }).then((r) => r.data),
  // POST /auth/login/password { email, password } -> { access_token, ... } (+cookie)
  passwordLogin: (email, password) =>
    dsHttp.post("/auth/login/password", { email, password }).then((r) => r.data),
  // POST /auth/me/password { new_password, current_password? } — the
  // authenticated user sets/changes their OWN password (verified against the LOS
  // OpenAPI: SelfPasswordRequest, `new_password` required, `current_password`
  // optional — OTP-only users who never had a password can omit it). Returns 200.
  // (Replaces the old `/auth/set-password` path, which the LOS does not serve.)
  setMyPassword: (newPassword, currentPassword) =>
    dsHttp
      .post("/auth/me/password", {
        new_password: newPassword,
        ...(currentPassword ? { current_password: currentPassword } : {}),
      })
      .then((r) => r.data),
  me: () => dsHttp.get("/auth/me").then((r) => r.data),
  // GET /auth/me/activity -> caller's own recent activity (up to 100 rows)
  myActivity: () => dsHttp.get("/auth/me/activity").then((r) => r.data),
  // POST /auth/introspect -> { active, session } (validate a token)
  introspect: () => dsHttp.post("/auth/introspect", {}).then((r) => r.data),
  logout: () => dsHttp.post("/auth/logout").then((r) => r.data),
  patchMe: (full_name) =>
    dsHttp.patch("/auth/me", { full_name }).then((r) => r.data),

  // ── New first-party auth contract (los-auth-migration.md) — additive ──
  // Reissues a fresh access token from the server-side session. No-op until
  // the new auth-service exposes it.
  refresh: () => dsHttp.post("/auth/refresh").then((r) => r.data),

  // Password reset (los-auth-migration.md §9). Request is always 202 (never
  // reveals account existence); verify gates the reset form; complete sets the
  // new password and auto-logs-in (returns a session/access token).
  requestPasswordReset: (email) =>
    dsHttp.post("/auth/password/reset/request", { email }).then((r) => r.data),
  verifyPasswordReset: (token) =>
    dsHttp
      .get(`/auth/password/reset/verify?token=${encodeURIComponent(token)}`)
      .then((r) => r.data),
  completePasswordReset: (token, newPassword) =>
    dsHttp
      .post("/auth/password/reset/complete", {
        token,
        new_password: newPassword,
      })
      .then((r) => r.data),
};

export default authApi;
